import { jwtDecode } from 'jwt-decode'
import i18next from 'i18next'

import { ensureValidAccessToken } from '@services/api-client'
import { userStorage } from '@services/utils/local-storage'
import { SNA_PREFIX } from '@services/utils'

import type { Apartment, IndexCategory } from './types'

function getAppApiBaseUrl(): string {
  const base = import.meta.env.VITE_BASE_URL || ''
  return `${base.replace(/\/$/, '')}${SNA_PREFIX}`
}

const SESSION_STORAGE_KEY = 'sna_analytics_session_id'

export type AnalyticsEventName =
  | 'filters_applied'
  | 'apartment_viewed'
  | 'apartment_clicked'
  | 'apartment_liked'

export type AnalyticsSource = 'list' | 'map' | 'chat' | 'detail'

function generateSessionId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID().replace(/-/g, '').slice(0, 32)
  }
  return `s${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`
}

export function getAnalyticsSessionId(): string {
  let id = localStorage.getItem(SESSION_STORAGE_KEY)
  if (!id || id.length < 8) {
    id = generateSessionId()
    localStorage.setItem(SESSION_STORAGE_KEY, id)
  }
  return id
}

/** Auth context merged into every tracked event when user has a JWT. */
export function getAnalyticsUserContext(): {
  is_authenticated: boolean
  user_id: string | null
} {
  const stored = userStorage.get()
  if (!stored?.accessToken) {
    return { is_authenticated: false, user_id: null }
  }

  try {
    const decoded = jwtDecode<{ user_id?: string }>(stored.accessToken)
    const userId = decoded.user_id ?? null
    return {
      is_authenticated: Boolean(userId),
      user_id: userId
    }
  } catch {
    return { is_authenticated: false, user_id: null }
  }
}

export function getAnalyticsGlobalProperties(): Record<string, unknown> {
  return {
    locale: i18next.language || 'sk',
    ...getAnalyticsUserContext()
  }
}

export function apartmentAnalyticsProps(
  apartment: Pick<
    Apartment,
    | 'id'
    | 'price_month'
    | 'room_count'
    | 'area_m2'
    | 'location'
    | 'index'
    | 'listing_type'
  >,
  source: AnalyticsSource,
  indexCategory?: IndexCategory
): Record<string, unknown> {
  return {
    apartment_id: apartment.id,
    price_month: apartment.price_month,
    room_count: apartment.room_count,
    area_m2: apartment.area_m2,
    location: apartment.location,
    listing_type: apartment.listing_type ?? 'rent',
    source,
    index_category: indexCategory ?? apartment.index?.category ?? null,
    index_score: apartment.index?.index ?? null
  }
}

export function trackEvent(
  event: AnalyticsEventName,
  properties?: Record<string, unknown>
): void {
  void (async () => {
    // This fetch cannot retry on 401 (fire-and-forget, keepalive), so make
    // sure a valid token is in storage before reading the bearer.
    await ensureValidAccessToken()

    const bearer = userStorage.getBearer()
    // Payload is built after the refresh so the user context decodes the
    // fresh token.
    const payload = {
      event,
      session_id: getAnalyticsSessionId(),
      properties: {
        ...getAnalyticsGlobalProperties(),
        ...properties
      }
    }

    // Same-origin BFF ingest — ad-blockers block cross-origin /analytics/events.
    await fetch(`${getAppApiBaseUrl()}event-analytics/ingest`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(bearer ? { Authorization: bearer } : {})
      },
      body: JSON.stringify(payload),
      keepalive: true
    })
  })().catch(() => {
    /* analytics must not break the app */
  })
}
